import AvailabilityUtils from "./availability/AvailabilityUtils";
import logger from "./logger";


const DURATION_KEYS = ["duration", "bookingDuration", "bookingPeriod", "billingPeriod"];

const normalizeDurationValue = value => {
  if (!value || typeof value !== "string") return null;
  return value.trim().toUpperCase();
};

export const getNormalizedDurationType = (spaceOrDuration, fallback = "HOURLY") => {
  if (!spaceOrDuration) return fallback;

  if (typeof spaceOrDuration === "string") {
    return normalizeDurationValue(spaceOrDuration) || fallback;
  }

  if (typeof spaceOrDuration === "object") {
    for (const key of DURATION_KEYS) {
      const value = spaceOrDuration[key];
      const normalized = normalizeDurationValue(value);
      if (normalized) return normalized;
    }
    const priceDuration = spaceOrDuration?.propertyPrice?.duration;
    const normalizedPrice = normalizeDurationValue(priceDuration);
    if (normalizedPrice) return normalizedPrice;
  }

  return fallback;
};

const normalizeDate = dateInput => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getDateKey = date => {
  const normalized = normalizeDate(date);
  if (!normalized) return null;
  return normalized.toISOString().split("T")[0];
};

const getWeekStartKey = date => {
  const normalized = normalizeDate(date);
  if (!normalized) return null;

  // ISO week starts on Monday
  const day = normalized.getDay();
  const diff = (day + 6) % 7; // convert Sunday(0) to 6, Monday(1) -> 0
  normalized.setDate(normalized.getDate() - diff);
  return normalized.toISOString().split("T")[0];
};

const getMonthKey = date => {
  const normalized = normalizeDate(date);
  if (!normalized) return null;
  const year = normalized.getFullYear();
  const month = (normalized.getMonth() + 1).toString().padStart(2, "0");
  return `${year}-${month}`;
};

const addToMap = (map, key, booking) => {
  if (!key) return;
  const list = map.get(key) || [];
  list.push(booking);
  map.set(key, list);
};

export const buildBookingTimeline = (bookings = []) => {
  const bookingsByDate = new Map();
  const bookingsByWeek = new Map();
  const bookingsByMonth = new Map();

  bookings.forEach(booking => {
    if (!booking?.startTime || !booking?.endTime) return;
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);
    if (isNaN(start) || isNaN(end)) return;

    const cursor = normalizeDate(start);
    const normalizedEnd = normalizeDate(end);
    if (!cursor || !normalizedEnd) return;

    while (cursor <= normalizedEnd) {
      const dateKey = getDateKey(cursor);
      addToMap(bookingsByDate, dateKey, booking);
      addToMap(bookingsByWeek, getWeekStartKey(cursor), booking);
      addToMap(bookingsByMonth, getMonthKey(cursor), booking);
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return {
    bookingsByDate,
    bookingsByWeek,
    bookingsByMonth,
  };
};

export const getBookingsForDate = (date, timeline) => {
  if (!timeline) return [];
  const key = getDateKey(date);
  if (!key) return [];
  return timeline.bookingsByDate.get(key) || [];
};

/**
 * Check if a date is fully booked based on duration type and category.
 * For DAILY bookings, checks if instances have bookings covering the full required duration.
 * @param {Date} date - The date to check
 * @param {Object} timeline - The booking timeline
 * @param {number} totalInstances - Total number of instances for the space
 * @param {string} durationType - Duration type (HOURLY, DAILY, etc.)
 * @param {string|object} category - Service category (WORK, STAY, PLAY)
 * @returns {boolean} True if the date is fully booked
 */
export const isDateBooked = (date, timeline, totalInstances = 1, durationType = "HOURLY", category = null) => {
  const bookings = getBookingsForDate(date, timeline);

  // For HOURLY bookings, group by instance ID to properly handle multi-instance spaces
  const normalizedDuration = (durationType || "HOURLY").toUpperCase();
  if (normalizedDuration === "HOURLY") {
    const busyInstanceIds = new Set();
    bookings.forEach(booking => {
      const instanceId = booking.spaceInstanceId;
      if (instanceId) {
        busyInstanceIds.add(instanceId);
      } else {
        busyInstanceIds.add(`booking_${booking.id}`);
      }
    });
    return busyInstanceIds.size >= totalInstances;
  }

  // For DAILY bookings, check if bookings cover the full required duration
  if (normalizedDuration === "DAILY" && category) {
    return isDateFullyBookedForDaily(date, bookings, totalInstances, category);
  }

  // Fallback to simple count for other duration types
  return bookings.length >= totalInstances;
};

/**
 * Check if a date is fully booked for DAILY duration bookings.
 * A date is fully booked when all instances have bookings that span the full required duration.
 * @param {Date} date - The date to check
 * @param {Array} bookings - Bookings for this date
 * @param {number} totalInstances - Total number of instances
 * @param {string|object} category - Service category
 * @returns {boolean} True if all instances are fully booked for the entire day
 */
const isDateFullyBookedForDaily = (date, bookings, totalInstances, category) => {
  if (!bookings || bookings.length === 0) return false;

  // Get time constraints for this category and DAILY duration
  const constraints = AvailabilityUtils.getTimeConstraints(category, "DAILY");
  if (!constraints) return bookings.length >= totalInstances;

  logger.log(`🔍 [DAILY VALIDATION] Checking date ${date.toISOString().split("T")[0]} for category:`, category);
  logger.log(`🔍 [DAILY VALIDATION] Time constraints:`, constraints);
  logger.log(`🔍 [DAILY VALIDATION] Total instances: ${totalInstances}, Bookings count: ${bookings.length}`);

  // Parse required start and end times
  const [requiredStartH, requiredStartM] = constraints.startTime.split(":").map(Number);
  const [requiredEndH, requiredEndM] = constraints.endTime.split(":").map(Number);

  // Create Date objects for the required time range
  const requiredStart = new Date(date);
  requiredStart.setHours(requiredStartH, requiredStartM, 0, 0);

  const requiredEnd = new Date(date);
  requiredEnd.setHours(requiredEndH, requiredEndM, 0, 0);

  // Handle overnight spans (e.g., STAY category: midnight to midnight next day)
  if (constraints.spansOvernight) {
    requiredEnd.setDate(requiredEnd.getDate() + 1);
  }

  logger.log(`🔍 [DAILY VALIDATION] Required range: ${requiredStart.toISOString()} to ${requiredEnd.toISOString()}`);

  // Group bookings by instance ID
  const instanceBookings = new Map();
  bookings.forEach(booking => {
    const instanceId = booking.spaceInstanceId;
    if (!instanceId) return;

    if (!instanceBookings.has(instanceId)) {
      instanceBookings.set(instanceId, []);
    }
    instanceBookings.get(instanceId).push(booking);
  });

  logger.log(`🔍 [DAILY VALIDATION] Unique instances with bookings: ${instanceBookings.size}`);

  // Count how many instances are fully booked
  let fullyBookedCount = 0;

  instanceBookings.forEach((instanceBookingList, instanceId) => {
    // Check if any booking for this instance covers the full required duration
    const hasFullDayBooking = instanceBookingList.some(booking => {
      const bookingStart = new Date(booking.startTime);
      const bookingEnd = new Date(booking.endTime);

      // Check if booking spans the full required duration
      // Booking must start at or before required start and end at or after required end
      const coversFullDay = bookingStart <= requiredStart && bookingEnd >= requiredEnd;

      logger.log(
        `🔍 [DAILY VALIDATION] Instance ${instanceId}: ${bookingStart.toISOString()} to ${bookingEnd.toISOString()} - Covers full day: ${coversFullDay}`
      );

      return coversFullDay;
    });

    if (hasFullDayBooking) {
      fullyBookedCount++;
      logger.log(`🔍 [DAILY VALIDATION] Instance ${instanceId} is FULLY BOOKED`);
    }
  });

  const isFullyBooked = fullyBookedCount >= totalInstances;
  logger.log(
    `🔍 [DAILY VALIDATION] Result: ${fullyBookedCount}/${totalInstances} instances fully booked - Date is ${isFullyBooked ? "FULLY BOOKED" : "AVAILABLE/PARTIAL"}`
  );

  // Date is fully booked if all instances are fully booked
  return isFullyBooked;
};

/**
 * Check if a week is fully booked for WEEKLY duration bookings.
 * A week is fully booked when all instances have bookings that span each day of the week
 * with the proper daily hours (e.g., 00:00-23:59 for both WORK and STAY).
 */
const isWeekFullyBookedForWeekly = (date, bookings, totalInstances, category) => {
  if (!bookings || bookings.length === 0) return false;

  // Get time constraints for this category and WEEKLY duration
  const constraints = AvailabilityUtils.getTimeConstraints(category, "WEEKLY");
  if (!constraints) return bookings.length >= totalInstances;

  logger.log(
    `🔍 [WEEKLY VALIDATION] Checking week containing ${date.toISOString().split("T")[0]} for category:`,
    category
  );
  logger.log(`🔍 [WEEKLY VALIDATION] Daily time constraints: ${constraints.startTime} - ${constraints.endTime}`);

  // Calculate week start and end
  const weekStart = new Date(date);
  const day = weekStart.getDay();
  const diff = (day + 6) % 7; // ISO week starts on Monday
  weekStart.setDate(weekStart.getDate() - diff);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 7);
  weekEnd.setHours(0, 0, 0, 0);

  logger.log(`🔍 [WEEKLY VALIDATION] Week range: ${weekStart.toISOString()} to ${weekEnd.toISOString()}`);

  // Parse daily time constraints
  const [dailyStartH, dailyStartM] = constraints.startTime.split(":").map(Number);
  const [dailyEndH, dailyEndM] = constraints.endTime.split(":").map(Number);

  // Group bookings by instance ID
  const instanceBookings = new Map();
  bookings.forEach(booking => {
    const instanceId = booking.spaceInstanceId;
    if (!instanceId) return;

    if (!instanceBookings.has(instanceId)) {
      instanceBookings.set(instanceId, []);
    }
    instanceBookings.get(instanceId).push(booking);
  });

  logger.log(`🔍 [WEEKLY VALIDATION] Unique instances with bookings: ${instanceBookings.size}`);

  // Count how many instances are fully booked for the week
  let fullyBookedCount = 0;

  instanceBookings.forEach((instanceBookingList, instanceId) => {
    // Check if bookings cover ALL 7 days with proper daily hours
    let allDaysCovered = true;

    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      const checkDate = new Date(weekStart);
      checkDate.setDate(checkDate.getDate() + dayOffset);

      // Required time range for this day
      const requiredStart = new Date(checkDate);
      requiredStart.setHours(dailyStartH, dailyStartM, 0, 0);

      const requiredEnd = new Date(checkDate);
      requiredEnd.setHours(dailyEndH, dailyEndM, 0, 0);

      // Handle overnight spans (e.g., STAY category)
      if (constraints.spansOvernight) {
        requiredEnd.setDate(requiredEnd.getDate() + 1);
      }

      // Check if any booking covers this day's required hours
      const dayIsCovered = instanceBookingList.some(booking => {
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);

        // Booking must start at or before required start and end at or after required end
        return bookingStart <= requiredStart && bookingEnd >= requiredEnd;
      });

      if (!dayIsCovered) {
        allDaysCovered = false;
        logger.log(
          `🔍 [WEEKLY VALIDATION] Instance ${instanceId}: Day ${dayOffset + 1} (${checkDate.toISOString().split("T")[0]}) NOT covered`
        );
        break;
      }
    }

    if (allDaysCovered) {
      fullyBookedCount++;
      logger.log(
        `🔍 [WEEKLY VALIDATION] Instance ${instanceId} is FULLY BOOKED (all 7 days covered with proper hours)`
      );
    }
  });

  const isFullyBooked = fullyBookedCount >= totalInstances;
  logger.log(
    `🔍 [WEEKLY VALIDATION] Result: ${fullyBookedCount}/${totalInstances} instances fully booked - Week is ${isFullyBooked ? "FULLY BOOKED" : "AVAILABLE/PARTIAL"}`
  );

  return isFullyBooked;
};

/**
 * Check if a month is fully booked for MONTHLY duration bookings.
 * A month is fully booked when all instances have bookings that span each day of the month
 * with the proper daily hours (e.g., 00:00-23:59 for both WORK and STAY).
 */
const isMonthFullyBookedForMonthly = (date, bookings, totalInstances, category) => {
  if (!bookings || bookings.length === 0) return false;

  // Get time constraints for this category and MONTHLY duration
  const constraints = AvailabilityUtils.getTimeConstraints(category, "MONTHLY");
  if (!constraints) return bookings.length >= totalInstances;

  logger.log(`🔍 [MONTHLY VALIDATION] Checking month ${date.toISOString().split("T")[0]} for category:`, category);
  logger.log(`🔍 [MONTHLY VALIDATION] Daily time constraints: ${constraints.startTime} - ${constraints.endTime}`);

  // Calculate month start and end
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  monthStart.setHours(0, 0, 0, 0);

  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  monthEnd.setHours(0, 0, 0, 0);

  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  logger.log(
    `🔍 [MONTHLY VALIDATION] Month range: ${monthStart.toISOString()} to ${monthEnd.toISOString()} (${daysInMonth} days)`
  );

  // Parse daily time constraints
  const [dailyStartH, dailyStartM] = constraints.startTime.split(":").map(Number);
  const [dailyEndH, dailyEndM] = constraints.endTime.split(":").map(Number);

  // Group bookings by instance ID
  const instanceBookings = new Map();
  bookings.forEach(booking => {
    const instanceId = booking.spaceInstanceId;
    if (!instanceId) return;

    if (!instanceBookings.has(instanceId)) {
      instanceBookings.set(instanceId, []);
    }
    instanceBookings.get(instanceId).push(booking);
  });

  logger.log(`🔍 [MONTHLY VALIDATION] Unique instances with bookings: ${instanceBookings.size}`);

  // Count how many instances are fully booked for the month
  let fullyBookedCount = 0;

  instanceBookings.forEach((instanceBookingList, instanceId) => {
    // Check if bookings cover ALL days in the month with proper daily hours
    let allDaysCovered = true;

    for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth++) {
      const checkDate = new Date(date.getFullYear(), date.getMonth(), dayOfMonth);

      // Required time range for this day
      const requiredStart = new Date(checkDate);
      requiredStart.setHours(dailyStartH, dailyStartM, 0, 0);

      const requiredEnd = new Date(checkDate);
      requiredEnd.setHours(dailyEndH, dailyEndM, 0, 0);

      // Handle overnight spans (e.g., STAY category)
      if (constraints.spansOvernight) {
        requiredEnd.setDate(requiredEnd.getDate() + 1);
      }

      // Check if any booking covers this day's required hours
      const dayIsCovered = instanceBookingList.some(booking => {
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);

        // Booking must start at or before required start and end at or after required end
        return bookingStart <= requiredStart && bookingEnd >= requiredEnd;
      });

      if (!dayIsCovered) {
        allDaysCovered = false;
        logger.log(
          `🔍 [MONTHLY VALIDATION] Instance ${instanceId}: Day ${dayOfMonth} (${checkDate.toISOString().split("T")[0]}) NOT covered`
        );
        break; // No need to check remaining days
      }
    }

    if (allDaysCovered) {
      fullyBookedCount++;
      logger.log(
        `🔍 [MONTHLY VALIDATION] Instance ${instanceId} is FULLY BOOKED (all ${daysInMonth} days covered with proper hours)`
      );
    }
  });

  const isFullyBooked = fullyBookedCount >= totalInstances;
  logger.log(
    `🔍 [MONTHLY VALIDATION] Result: ${fullyBookedCount}/${totalInstances} instances fully booked - Month is ${isFullyBooked ? "FULLY BOOKED" : "AVAILABLE/PARTIAL"}`
  );

  return isFullyBooked;
};

/**
 * Check if a week is fully booked based on duration type and category.
 * For WEEKLY bookings, checks if instances have bookings covering the full week.
 * @param {Date} date - Any date within the week to check
 * @param {Object} timeline - The booking timeline
 * @param {number} totalInstances - Total number of instances for the space
 * @param {string} durationType - Duration type (should be WEEKLY)
 * @param {string|object} category - Service category (WORK, STAY, PLAY)
 * @returns {boolean} True if the week is fully booked
 */
export const isWeekBooked = (date, timeline, totalInstances = 1, durationType = "WEEKLY", category = null) => {
  if (!timeline) return false;
  const key = getWeekStartKey(date);
  if (!key) return false;
  const bookings = timeline.bookingsByWeek.get(key) || [];

  // For WEEKLY bookings with category, check full-week coverage
  const normalizedDuration = (durationType || "WEEKLY").toUpperCase();
  if (normalizedDuration === "WEEKLY" && category) {
    return isWeekFullyBookedForWeekly(date, bookings, totalInstances, category);
  }

  // Fallback to simple count
  return bookings.length >= totalInstances;
};

/**
 * Check if a month is fully booked based on duration type and category.
 * For MONTHLY bookings, checks if instances have bookings covering the full month.
 * @param {Date} date - Any date within the month to check
 * @param {Object} timeline - The booking timeline
 * @param {number} totalInstances - Total number of instances for the space
 * @param {string} durationType - Duration type (should be MONTHLY)
 * @param {string|object} category - Service category (WORK, STAY, PLAY)
 * @returns {boolean} True if the month is fully booked
 */
export const isMonthBooked = (date, timeline, totalInstances = 1, durationType = "MONTHLY", category = null) => {
  if (!timeline) return false;
  const key = getMonthKey(date);
  if (!key) return false;
  const bookings = timeline.bookingsByMonth.get(key) || [];

  // For MONTHLY bookings with category, check full-month coverage
  const normalizedDuration = (durationType || "MONTHLY").toUpperCase();
  if (normalizedDuration === "MONTHLY" && category) {
    return isMonthFullyBookedForMonthly(date, bookings, totalInstances, category);
  }

  // Fallback to simple count
  return bookings.length >= totalInstances;
};

export const shouldDisableDateForDuration = (durationType, date, timeline, totalInstances = 1, category = null) => {
  if (!date) return true;
  if (!timeline) return false;

  const type = (durationType || "HOURLY").toUpperCase();
  if (type === "HOURLY") {
    return false;
  }

  // For Daily/Weekly/Monthly, disable booked days only if all instances are taken
  return isDateBooked(date, timeline, totalInstances, durationType, category);
};

export const getBookingsOverlaySummary = (durationType, date, timeline) => {
  const type = (durationType || "HOURLY").toUpperCase();

  switch (type) {
    case "WEEKLY":
      return {
        type: "week",
        bookings: isWeekBooked(date, timeline) ? timeline.bookingsByWeek.get(getWeekStartKey(date)) || [] : [],
      };
    case "MONTHLY":
      return {
        type: "month",
        bookings: isMonthBooked(date, timeline) ? timeline.bookingsByMonth.get(getMonthKey(date)) || [] : [],
      };
    default:
      return {
        type: "day",
        bookings: getBookingsForDate(date, timeline),
      };
  }
};

/**
 * Formats a duration/billing period string into a human-readable "per [period]" format.
 * @param {string} duration - The duration type (HOURLY, DAILY, WEEKLY, MONTHLY)
 * @returns {string} Formatted string (e.g., "per hour", "per day")
 */
export const formatDurationType = duration => {
  if (!duration) return "";

  const type = duration.toUpperCase();
  switch (type) {
    case "HOURLY":
      return "per hour";
    case "DAILY":
      return "per day";
    case "WEEKLY":
      return "per week";
    case "MONTHLY":
      return "per month";
    default:
      // Fallback for other potential types, converting to lowercase and adding "per "
      return `per ${duration.toLowerCase()}`;
  }
};

/**
 * Formats a duration string into a single unit (e.g., "hour", "day").
 * @param {string} duration - The duration type (HOURLY, DAILY, WEEKLY, MONTHLY)
 * @returns {string} Formatted unit
 */
export const formatDurationUnit = duration => {
  if (!duration) return "";
  const type = duration.toUpperCase();
  switch (type) {
    case "HOURLY":
      return "hour";
    case "DAILY":
      return "day";
    case "WEEKLY":
      return "week";
    case "MONTHLY":
      return "month";
    default:
      return type.toLowerCase();
  }
};
